/**
 * The Main Menu, as one `BoardSpec`.
 *
 * Every number here was measured pixel-by-pixel off the ui-reference lane's
 * DOSBox-X capture of the real `PS.EXE` Main Menu
 * (`docs/game/ui-reference/main-menu.png`, a clean 2× shot of the 320×200
 * frame). `PS.EXE`'s menu screens are hardcoded — the six `.BOX` resources in
 * the game cover dialogs only, never these — so the screenshot *is* the
 * specification, exactly as the lane brief states.
 *
 * The widget those measurements describe now lives in `board-chrome.ts`,
 * because the same board reappears at two other sizes in `career-menu.png`
 * and `fly-single-mission.png`. What is left here is this board's own numbers
 * and the exports the rest of the shell already reaches for.
 */

import {
  bannerRectOf,
  boardFieldSpansOf,
  boardOps,
  boardRows,
  boardSlotRect,
  type BoardSpec,
  BUTTON_HEIGHT,
  BUTTON_PITCH,
  ORNAMENT_SHEET,
} from "./board-chrome.ts";
import type { DrawOp, FontId, Rect, Span, TextStyle } from "./draw-ops.ts";
import type { FontAtlas } from "./text-metrics.ts";

export { BUTTON_HEIGHT, BUTTON_PITCH, ORNAMENT_SHEET };

export const MAIN_MENU_BACKGROUND = "screens/MAINMENU.SCR";

export const BOARD: Rect = { x: 6, y: 18, w: 148, h: 165 };

export const BUTTON_X = 17;
export const BUTTON_WIDTH = 126;
export const BUTTON_FIRST_Y = 38;
/** Labels centre on x=81, not the button's own 80 — measured, and it reproduces all seven capture positions. */
export const BUTTON_LABEL_CENTER = 81;
export const BUTTON_FONT: FontId = "FONT1";

/** `MAIN MENU` sits at cell (51,3) in the capture; 84 is the centre that rule reproduces. */
export const BANNER_TEXT_CENTER = 84;
export const BANNER_TEXT_Y = 3;
export const BANNER_FONT: FontId = "FONT2";
/** The banner legend is flat black (`PS.PAL` 0) — matched glyph-for-glyph against the capture at IoU 1.000. */
export const BANNER_TEXT_STYLE: TextStyle = { body: 0 };

/** Ridge and footer bar insets: measured, not derived — see `board-chrome.ts`'s header. */
export const MAIN_MENU_BOARD: BoardSpec = {
  frame: BOARD,
  rows: boardRows(7),
  columns: 1,
  columnWidth: BUTTON_WIDTH,
  columnGap: 0,
  ridgeInsets: [21, 33],
  footerInsets: [27, 45],
};

export function buttonRect(index: number): Rect {
  return boardSlotRect(MAIN_MENU_BOARD, index);
}

export function boardFieldSpans(): Span[] {
  return boardFieldSpansOf(BOARD);
}

/** The banner rectangle the capture shows: x=44..121, resting on the board's top edge. */
export function mainMenuBannerRect(font: FontAtlas): Rect {
  return bannerRectOf(BOARD, "MAIN MENU", font);
}

export interface BoardRenderInput {
  readonly title: string;
  readonly labels: readonly string[];
  readonly selected: number;
  readonly font: FontAtlas;
  readonly bannerFont: FontAtlas;
}

export function mainMenuOps(input: BoardRenderInput): DrawOp[] {
  return [
    { kind: "image", asset: MAIN_MENU_BACKGROUND, x: 0, y: 0 },
    ...boardOps(MAIN_MENU_BOARD, {
      title: input.title,
      buttons: input.labels.map((label) => ({ label, enabled: true })),
      selected: input.selected,
      font: input.font,
      bannerFont: input.bannerFont,
    }),
  ];
}
